import { apiClient } from './api-client';
import { PetResponse } from '../dto/pets/PetResponse';
import { ErrorResponse } from '../dto/common/ErrorResponse';

/**
 * Pet service.
 * Handles pet CRUD operations.
 */
class PetService {
  /**
   * Gets static vaccination dictionary entries.
   * @returns {Promise<{success: boolean, data?: Array, error?: ErrorResponse}>}
   */
  async getVaccinationCatalog() {
    try {
      const response = await apiClient.get('/pets/vaccinations/catalog');
      return { success: true, data: response.data || [] };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  /**
   * Gets all pets.
   * @returns {Promise<{success: boolean, data?: PetResponse[], error?: ErrorResponse}>}
   */
  async getPets() {
    try {
      const response = await apiClient.get('/pets');
      const pets = response.data.map(item => PetResponse.fromJSON(item));
      return { success: true, data: pets };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  /**
   * Gets pet details by ID.
   * @param {string} petId - Pet UUID
   * @returns {Promise<{success: boolean, data?: PetResponse, error?: ErrorResponse}>}
   */
  async getPetDetails(petId) {
    try {
      const response = await apiClient.get(`/pets/${petId}`);
      const pet = PetResponse.fromJSON(response.data);
      return { success: true, data: pet };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  /**
   * Creates a new pet.
   * @param {CreatePetRequest} request - Pet creation data
   * @returns {Promise<{success: boolean, data?: PetResponse, error?: ErrorResponse}>}
   */
  async createPet(request) {
    try {
      const response = await apiClient.post('/pets', request.toJSON());
      const pet = PetResponse.fromJSON(response.data);
      return { success: true, data: pet };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  /**
   * Updates an existing pet.
   * @param {string} petId - Pet UUID
   * @param {UpdatePetRequest} request - Pet update data
   * @returns {Promise<{success: boolean, data?: PetResponse, error?: ErrorResponse}>}
   */
  async updatePet(petId, request) {
    try {
      const response = await apiClient.put(`/pets/${petId}`, request.toJSON());
      const pet = PetResponse.fromJSON(response.data);
      return { success: true, data: pet };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  /**
   * Deletes a pet.
   * @param {string} petId - Pet UUID
   * @returns {Promise<{success: boolean, error?: ErrorResponse}>}
   */
  async deletePet(petId) {
    try {
      await apiClient.delete(`/pets/${petId}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }
}

export default new PetService();
